import { Component, Input, Output, EventEmitter, computed, signal, OnInit, inject, DestroyRef, effect } from '@angular/core';
import { SchemaField, Document, DocumentSchema, SystemFieldType } from '../../../store/models/document.model';
import { DynamicField } from '../dynamic-field';
import { ViewComponent } from '../../../store/models/workflow.model';
import { DocumentReader } from 'src/app/modules/core/store/models';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
  selector: 'app-phone-field',
  templateUrl: './phone-field.component.html',
  styleUrls: ['./phone-field.component.scss'],
  standalone: false
})
export class PhoneFieldComponent implements DynamicField, OnInit {
  @Output() valueChanged = new EventEmitter<DynamicField>();
  @Input() set field(value: SchemaField) {
    this._field = value;
  }
  get field(): SchemaField {
    return this._field;
  }
  _field: SchemaField;
  @Input() displayName: string;
  @Input() layerUId: string;
  @Input() set document(value: Document) {
    this.reader = new DocumentReader(value);
  }
  get document(): Document {
    return this.reader.document;
  }
  reader: DocumentReader;
  @Input() viewComponent: ViewComponent;
  @Input() schema: DocumentSchema;
  @Input() dataset: Dataset;

  private destroyRef = inject(DestroyRef);

  @Input() set isEdit(edit: boolean) {
    this._isEdit.set(edit);
  }

  private _isEdit = signal(false);
  isReadOnly = signal(false);
  showFieldAsEdit = computed(() => {
    const edit = this._isEdit();
    const readonly = this.isReadOnly();

    return edit && !readonly;
  });

  set value(val: string) {
    if (!val) {
      val = '';
    }

    this._value = val;
  }
  get value(): string {
    if (!this._value) {
      this._value = '';
    }

    return this._value;
  }
  private _value: string;

  control = new FormControl('');

  readonly controlDisabledState = effect(() => {
    const enabled = this.showFieldAsEdit();

    if (enabled) {
      this.control.enable();
    } else {
      this.control.disable();
    }
  });

  blurredOnce = false;
  isError = false;

  ngOnInit(): void {
    this.blurredOnce = this.value !== ''; // show error immediately if has initial value but is invalid phone number

    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
      this._value = val;
      this.valueChanged.emit(this);
    });

    this.control.statusChanges.pipe(debounceTime(10), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      this.isError = status === 'INVALID' || !!this.control.errors;
    });
  }

  handleBlur(): void {
    const { dirty, touched, invalid } = this.control;

    if (dirty && touched) {
      this.isError = invalid;

      if (!this.blurredOnce) {
        this.blurredOnce = true;
      }
    }
  }

  tpOnContentUpdated(): void {
    this.value = this.reader.getFieldValue(this.field?.systemName);
    this.control.patchValue(this.value);

    if ((this.field.fieldType & SystemFieldType.Editable) !== SystemFieldType.Editable || this.viewComponent?.configuration?.isReadOnly) {
      this.isReadOnly.set(true);
    }
  }
}
